import { VPCEvent } from '~src/commonInfra/services/VPCEvent';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';

export const DEFAULT_CARD_ZOOM_PERCENTAGE = 1;
export const CardZoomPercentageLocalStorageKeyPrefix = 'CardZoomPercentage';
export const CustomThumbnailTextTestId = 'CustomThumbnailTextTestId';

export class CardResizerService {
    public static instance = new CardResizerService();

    public cardResizeSliderVisibilityChanged = new VPCEvent<{ isCardSliderVisible: boolean }>();

    private isCardResizingFeatureAvailableOnUI = false;
    private cardZoomPercentageChanged = new VPCEvent<number>();
    public readonly CARD_RESIZE_SLIDER_MIN_VALUE = 0.6;
    public readonly CARD_RESIZE_SLIDER_MAX_VALUE = 1.4;

    private persistedCardZoomPercentage: number;

    constructor() {
        const sessionManagementService = getSessionManagementServiceInstance();
        sessionManagementService.bootstrapSessionPromise.then(() => {
            this.loadPersistedCardZoomPercentage();
        });

        this.cardResizeSliderVisibilityChanged.listenTo(args => {
            this.isCardResizingFeatureAvailableOnUI = args.isCardSliderVisible;
            this.updateCardZoomPercentage(this.getCardZoomPercentage());
        });
    }

    private loadPersistedCardZoomPercentage() {
        this.persistedCardZoomPercentage = DEFAULT_CARD_ZOOM_PERCENTAGE;

        const storedCardZoomPercentage = localStorage.getItem(this.getCardZoomPercentageLocalStorageKey());
        if (storedCardZoomPercentage !== null) {
            const cardZoomPercentage = Number(storedCardZoomPercentage);
            if (!isNaN(cardZoomPercentage) && cardZoomPercentage >= this.CARD_RESIZE_SLIDER_MIN_VALUE && cardZoomPercentage <= this.CARD_RESIZE_SLIDER_MAX_VALUE) {
                this.persistedCardZoomPercentage = cardZoomPercentage;
            }
        }
    }
    private getCardZoomPercentageLocalStorageKey(): string {
        const sessionManagementService = getSessionManagementServiceInstance();
        return CardZoomPercentageLocalStorageKeyPrefix + '-' + sessionManagementService.sessionUserLuid();
    }

    public getCardZoomPercentage(): number {
        return this.isCardResizingFeatureAvailableOnUI ? this.persistedCardZoomPercentage ?? DEFAULT_CARD_ZOOM_PERCENTAGE : DEFAULT_CARD_ZOOM_PERCENTAGE;
    }

    public getCardZoomPercentageForCustomThumbailText(): number {
        // This method is used to get a special zoom value for the text in custom thumbnail text.
        // The text in custom thumbnail should zoom along with the whole thumbnail but with a slower speed.
        // Then when the thubnail is at its smalleast size, the text is also zoomed to a smaller size but not as much as the thumbnail.
        // So the text can still maintain a readable font size at around 9px.
        return 1 / Math.pow(this.getCardZoomPercentage(), 0.7);
    }

    public updateCardZoomPercentage(newCardZoomPercentage: number, saveToLocalStorage?: boolean) {
        if (saveToLocalStorage) {
            this.persistedCardZoomPercentage = newCardZoomPercentage;
            localStorage.setItem(this.getCardZoomPercentageLocalStorageKey(), String(newCardZoomPercentage));
        }
        CardResizerService.instance.cardZoomPercentageChanged.trigger(newCardZoomPercentage);
    }

    public registerListener(listener: (cardZoomPercentage: number) => void): () => void {
        return CardResizerService.instance.cardZoomPercentageChanged.listenTo(listener);
    }

    public shouldUseCompactCardTitle() {
        return this.getCardZoomPercentage() < 0.8;
    }
}
